import { useMutation } from "@tanstack/react-query";
import { get } from "lodash-es";
import { Clip, hasClipSlotContent } from "@/shared/models/Clip";
import {
	createClip,
	setClipPunchSlots,
	updateClip,
} from "@/shared/repositories/ClipRepository";

const fieldToParamMap: Record<string, string> = {
	ClipPartNumber: "ClipPartNumber",
	CustomerClipPartNumber: "CustomerClipPartNumber",
	Width: "Width",
	Height: "Height",
	Gauge: "Gauge",
	CoilGauge: "CoilGauge",
	CoilWidth: "CoilWidth",
	WidthTolerance: "WidthTolerance",
	HeightTolerance: "HeightTolerance",
	MaterialGradeId: "MaterialGradeId",
	TestCheckCycle: "TestCheckCycle",
	DrawingNumber: "DrawingNumber",
	DrawingPath: "DrawingPath",
	DrawingRevision: "DrawingRevision",
	CustomerDrawingNumber: "CustomerDrawingNumber",
	CustomerDrawingRevision: "CustomerDrawingRevision",
	HolePattern: "HolePattern",
	SideGage: "SideGage",
	EntryKeeperId: "EntryKeeperId",
	ExitKeeperId: "ExitKeeperId",
	BottomKeeperSetup: "BottomKeeperSetup",
	DieSetDrawingNumber: "DieSetDrawingNumber",
	DieSetDrawingRevision: "DieSetDrawingRevision",
	DieSetNumber: "DieSetNumber",
	Comments: "Comments",
	IsActive: "Enabled",
};

export type ClipMutationResult = {
	ok: boolean;
	message: string;
	parentSucceeded?: boolean;
	slotsFailed?: boolean;
	clipId?: number;
};

type UseClipMutationsOptions = {
	mode: "create" | "update";
	getDirtyValues: () => Partial<Clip>;
	isSlotsDirty: () => boolean;
	isSlotsRetryPending: () => boolean;
	onSuccess: (message: string, clipId: number) => void;
	onError: (error: string) => void;
	onSlotsError: (message: string, clipId: number) => void;
};

export function useClipMutations({
	mode,
	getDirtyValues,
	isSlotsDirty,
	isSlotsRetryPending,
	onSuccess,
	onError,
	onSlotsError,
}: UseClipMutationsOptions) {
	const upsertMutation = useMutation<ClipMutationResult, Error, Clip>({
		mutationFn: async (data: Clip) => {
			const retryPending = isSlotsRetryPending();
			const dirtyValues = getDirtyValues();

			let resolvedClipId: number | null = data.ClipId ?? null;

			// Step 1: persist parent.
			// - create: always (unless we're already in retry mode for slots, which
			//   only happens when a previous create succeeded — see swap-to-update below).
			// - update: only when there are dirty parent fields and we're not retrying slots.
			if (!retryPending) {
				if (mode === "create") {
					const r = await createClip(data);
					if (!r.ok) {
						return {
							ok: false,
							message: r.message ?? "Failed to create clip",
							parentSucceeded: false,
						};
					}
					const newId = get(r, "data.tables[0].rows[0].ClipId", null) as
						| number
						| null;
					if (!newId) {
						return {
							ok: false,
							message: "Create succeeded but no ClipId was returned",
							parentSucceeded: false,
						};
					}
					resolvedClipId = newId;
				} else {
					if (!data.ClipId) {
						throw new Error("ClipId is required for update");
					}
					const fields: Record<string, string | number | boolean | null> = {};
					for (const [formField, paramName] of Object.entries(
						fieldToParamMap,
					)) {
						if (formField in dirtyValues) {
							fields[paramName] = dirtyValues[formField as keyof Clip] as
								| string
								| number
								| boolean
								| null;
						}
					}
					// Required BIT params (no default in SP): always send the current value.
					const formValues = data;
					fields["BottomKeeperSetup"] = formValues.BottomKeeperSetup;
					fields["Enabled"] = formValues.IsActive;
					const r = await updateClip(data.ClipId, fields);
					if (!r.ok) {
						return {
							ok: false,
							message: r.message ?? "Failed to save clip",
							parentSucceeded: false,
						};
					}
				}
			}

			if (resolvedClipId == null) {
				throw new Error("ClipId could not be resolved");
			}

			// Step 2: SetClipPunchSlots.
			// - On create: always (the form always has at least one slot row; the SP
			//   filters out fully-empty rows server-side).
			// - On update: when slots are dirty OR we're retrying after a previous failure.
			const shouldSyncSlots =
				retryPending ||
				isSlotsDirty() ||
				(mode === "create" && data.Slots.some(hasClipSlotContent));

			if (shouldSyncSlots) {
				try {
					const r = await setClipPunchSlots(resolvedClipId, data.Slots);
					if (!r.ok) {
						return {
							ok: false,
							message: r.message ?? "Failed to save die setup rows",
							parentSucceeded: true,
							slotsFailed: true,
							clipId: resolvedClipId,
						};
					}
				} catch (e) {
					const msg = e instanceof Error ? e.message : "Unknown error";
					return {
						ok: false,
						message: msg,
						parentSucceeded: true,
						slotsFailed: true,
						clipId: resolvedClipId,
					};
				}
			}

			return {
				ok: true,
				message: "The clip was saved successfully",
				clipId: resolvedClipId,
			};
		},
		onSuccess: (response) => {
			if (response.ok) {
				onSuccess(response.message, response.clipId!);
			} else if (response.slotsFailed && response.clipId != null) {
				onSlotsError(
					`Die setup rows failed: ${response.message}`,
					response.clipId,
				);
			} else {
				onError(response.message);
			}
		},
		onError: (error: Error) => {
			onError(error.message);
		},
	});

	return {
		upsertMutation,
		isPending: upsertMutation.isPending,
	};
}
